<template>
  <div class="flex justify-center items-center p-1 pr-0">
    <div
      class="picker-gradient"
      :style="{
        color: `hsl(${hue}, 100%, 50%)`,
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
      }"
      @mousedown="(e) => moveMarker(e, (e.buttons & 1) != 0)"
      @mousemove="(e) => moveMarker(e, (e.buttons & 1) != 0)"
      @mouseup="(e) => moveMarker(e, e.button == 1)"
      @mouseleave="(e) => moveMarker(e, (e.buttons & 1) != 0)"
    >
      <div
        class="picker-marker"
        :style="{
          'background-color': hexColor,
          left: `${markerX}px`,
          top: `${markerY}px`,
        }"
      ></div>
    </div>

    <div class="rotate-90 w-8 self-start origin-center ml-1 my-3">
      <div
        class="hue-slider h-3"
        :style="{
          width: `${(canvasWidth - 4).toFixed()}px`,
        }"
      >
        <input
          class="hue-slider-input"
          type="range"
          min="0"
          max="360"
          step="1"
          v-model="hue"
        />

        <div
          class="hue-marker"
          :style="{
            left: `${(hue / 360) * 100}%`,
            color: `hsl(${hue}, 100%, 50%)`,
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { colord } from "colord";
import { RGB8, rgb8AsDict, rgb8AsArray } from "@/keyboard";

const props = defineProps<{
  modelValue: RGB8;
}>();

const emit = defineEmits<{
  (_e: "update:modelValue", _value: RGB8): void;
}>();

const canvasWidth = 200;
const canvasHeight = canvasWidth;

const color = computed(() => colord(rgb8AsDict(props.modelValue)));
const hexColor = computed(() => color.value.toHex());
const colorHsv = computed(() => color.value.toHsv());

// Hue must be kept separately to avoid random changes. Just use correct initial value.
const hue = ref(colorHsv.value.h);

// Must keep marker position to avoid marker jumps due to quantization
const markerX = ref((colorHsv.value.s / 100) * canvasWidth);
const markerY = ref((1 - colorHsv.value.v / 100) * canvasHeight);

onMounted(() => {
  hue.value = colorHsv.value.h;
  markerX.value = (colorHsv.value.s / 100) * canvasWidth;
  markerY.value = (1 - colorHsv.value.v / 100) * canvasHeight;
});

const clamp = (val: number, min: number, max: number) =>
  Math.max(min, Math.min(max, val));

const outputColor = computed(() => {
  const [x, y] = [
    clamp(markerX.value / canvasWidth, 0, 1),
    clamp(markerY.value / canvasHeight, 0, 1),
  ];
  const hsv = {
    h: hue.value,
    s: x * 100,
    v: (1 - y) * 100,
  };
  return colord(hsv);
});

// Covers all color changes as outputColor changes when xy change OR hue changes.
watch(outputColor, (color) => {
  const rgb8 = rgb8AsArray(color.toRgb());
  emit("update:modelValue", rgb8);
});

const moveMarker = (e: MouseEvent, condition: boolean) => {
  if (!condition) return;

  const elem = e.target as HTMLElement;
  const rect = elem.getBoundingClientRect();

  markerX.value = e.x - rect.left;
  markerY.value = e.y - rect.top;
};
</script>

<style scoped>
.picker-gradient {
  @apply relative cursor-pointer z-10;
  background-image: linear-gradient(rgba(0, 0, 0, 0), #000),
    linear-gradient(90deg, #fff, currentColor);
}

.picker-marker {
  @apply cursor-pointer pointer-events-none;
  @apply rounded-full border-2 border-white;
  @apply absolute w-5 h-5 -translate-y-1/2 -translate-x-1/2; /* FIXME: hard-coded x translation depending on hue-slider width */
  @apply shadow;
}

.hue-slider {
  @apply relative p-0 rounded-full;
  background-image: linear-gradient(
    to right,
    #f00 0%,
    #ff0 16.66%,
    #0f0 33.33%,
    #0ff 50%,
    #00f 66.66%,
    #f0f 83.33%,
    #f00 100%
  );
}

.hue-slider-input {
  @apply opacity-0 bg-transparent appearance-none outline-none;
  @apply m-0 w-full h-full;
  @apply cursor-pointer;
  @apply absolute left-0 top-0;
}

.hue-marker {
  @apply cursor-pointer pointer-events-none;
  @apply bg-current rounded-full border-2 border-white;
  @apply absolute top-1/2 w-5 h-5 -translate-x-1/2 -translate-y-1/2;
  @apply shadow;
}
</style>
